import * as Commons from "oc-commons/Commons";
import * as Maths from "oc-commons/Maths";
import { Pfosten } from "../arrangement/Pfosten";
import { PfostenWinkelInput } from "../arrangement/pfostenFunctions";
import { MPfosten } from "../../metamodell";

let pi: number = Math.PI;
let fullCircle = 2 * pi;

export function setPiForTests(value: number) {
  pi = value;
  fullCircle = 2 * pi;
}

/**
 * "": Seite des Pfostens ohne Besonderheiten
 * "E": Eckadapter
 * "U": UMProfil
 */
export type AngleIntervalType = "" | "E" | "U";

export type AngleInterval = [number, number, AngleIntervalType];

export const istEckadapter = (type: AngleIntervalType) => type === "E";
export const istUMProfil = (type: AngleIntervalType) => type === "U";
// Eckadapter oder UMProfile kriegen einen negativen Wert
export const typeScore = (type: AngleIntervalType) =>
  (istEckadapter(type) ? -2 : 0) + (istUMProfil(type) ? -1 : 0);
export const flacheSeitePasst = (angle: PfostenWinkelInput, type: AngleIntervalType) =>
  angle.flacheSeiteNotwendig && !istEckadapter(type);

export const roundAngle = (x) => Maths.round(x, 10);

/**
 * normalisiert den angle (Bogenmaß) im Interval zwischen 0 und 2 * PI (360°)
 */
export function normalizeAngle(angle: number) {
  return ((angle % fullCircle) + fullCircle) % fullCircle;
}

/**
 * normalisiert den angle (Bogenmaß) im Interval zwischen -PI (-180°) und +PI (+180°),
 * was für das Vergleichen von Winkeln praktischer ist
 */
export function normalizeHalfCircle(angle: number) {
  const a = normalizeAngle(angle);
  return a < pi ? a : a - fullCircle;
}

// ACHTUNG! Winkelintervalle müssen kleiner 360 Grad Breit sein!
// Einen Vollkreis können sie nicht abdecken, aufgrund der Normalisierung.

/**
 * Berechnet den Winkelunterschied von l nach r
 * @param l Linke Intervallgrenze in Radiant
 * @param r Rechte Intervallgrenze in Radiant
 * @returns Winkelunterschied in Radiant
 */
export function deltaAngle(l: number, r: number): number {
  l = normalizeAngle(l);
  r = normalizeAngle(r);
  if (l <= r) {
    // 0    l---->r    2π
    return r - l;
  } else {
    // 0--->r     l--->2π
    return 2 * pi - (l - r);
  }
}

/**
 * Berechnet den Winkel, der genau zwischen zwei Winkeln liegt
 * @param l Linke Intervallgrenze in Radiant
 * @param r Rechte Intervallgrenze in Radiant
 */
export function middleAngle(l: number, r: number): number {
  return normalizeAngle(l + deltaAngle(l, r) / 2);
}

/**
 * Überprüft, ob ein Winkel λ zwischen l und r liegt
 * @param a Winkel in Radiant
 * @param l Linke Intervallgrenze in Radiant
 * @param r Rechte Intervallgrenze in Radiant
 * @returns True, wenn λ im Intervall liegt, sonst False
 */
export function angleInsideInterval(a: number, l: number, r: number, epsilon = 0.00001): boolean {
  a = normalizeAngle(a);
  l = normalizeAngle(l) - epsilon;
  r = normalizeAngle(r) + epsilon;

  if (l <= r) {
    // 0    l---->r    2π
    return l <= a && a <= r;
  } else {
    // 0--->r     l--->2π
    return !(r < a && a < l);
  }
}

/**
 * Berechnet die Schnittmenge zweier Winkelintervalle, auch wenn sie über 0 oder 2π gehen,
 * oder null, falls keine Schnittmenge existiert
 */
export function intervalIntersection(
  [l1, r1]: number[],
  [l2, r2]: number[]
): [number, number] | null {
  const il1 = angleInsideInterval(l1, l2, r2);
  const ir1 = angleInsideInterval(r1, l2, r2);
  const il2 = angleInsideInterval(l2, l1, r1);
  const ir2 = angleInsideInterval(r2, l1, r1);
  if (!(il1 || ir1 || il2 || ir2)) {
    return null;
  }
  return [il1 ? l1 : l2, ir1 ? r1 : r2].map(normalizeAngle) as [number, number];
}

/**
 * Verschiebt ein Intervall um einen Skalarwert, unter Berücksichtigung der zyklischen Natur von Winkeln.
 * @param interval Intervall [start, end]
 * @param scalar Verschiebungswert in Radiant
 * @returns Verschobenes Intervall [start, end]
 */
export function intervalAddScalar(interval: [number, number], scalar: number): [number, number] {
  const start = normalizeAngle(interval[0] + scalar);
  const end = normalizeAngle(interval[1] + scalar);
  return [start, end];
}

/**
 * Parst die Winkelintervalldefinition in Grad und gibt AngleInterval[] sortiert zurück.
 * Dabei steht E für Eckadapter und U für UM-Profil.
 * @example
 * "0°-90°E" ist das Intervall [0,90,"E"], während
 * "0°, 90°E" die Punkt-Intervalle [[0,0,null], [90,90,"E"]] entspricht.
 * @param angleIntervalDefinition Ein String mit Winkelintervallen oder ein Array von Winkelintervallen
 * @param minimumDegree Minimale Länge eines Intervalls in Grad. Standardwert ist 0.5
 * @returns Array von Winkel-Intervallen im Bogenmaß
 */
export function parseAngleIntervalDefinition(
  angleIntervalDefinition: string,
  minimumDegree = 0.5
): AngleInterval[] {
  if (Commons.isEmpty(angleIntervalDefinition)) {
    return [];
  }

  const intervals: AngleInterval[] = angleIntervalDefinition
    .split(/\s*,\s*/)
    .flatMap((x: string) => {
      const E = x.includes("E") ? "E" : null;
      const U = x.includes("U") ? "U" : null;
      const type = (U ?? E) as AngleIntervalType;
      const parts = x
        .replace(/[EU°]/g, "")
        .split(/\s*[-]\s*/)
        .map((part) => normalizeAngle((parseFloat(part) * pi) / 180));
      if (parts.length === 1) {
        parts.push(parts[0]);
      }
      const [l, r] = parts.toSorted();
      return [[l, r, type]];
    });

  return sortAngleIntervals(ensureTolerance(intervals, minimumDegree));
}

/**
 * Stellt die Minimalgröße jedes Intervalls sicher
 */
export function ensureTolerance(
  intervals: AngleInterval[],
  minimumDegree: number
): AngleInterval[] {
  const tolerance: number = (minimumDegree * pi) / 180;
  return intervals.map(([start, end, type]) => {
    if (deltaAngle(start, end) < tolerance) {
      const middle: number = (start + end) / 2;
      start = normalizeAngle(middle - tolerance / 2);
      end = normalizeAngle(middle + tolerance / 2);
    }
    return [start, end, type];
  });
}

/**
 * Sortiere die Intervalle nach dem Startwinkel
 */
export function sortAngleIntervals(intervalsRad: AngleInterval[]): AngleInterval[] {
  const order = ([l, r]: AngleInterval) => middleAngle(l, r);
  return intervalsRad.toSorted((a, b) => order(a) - order(b));
}

export interface MatchingRotation {
  /**
   * Winkel in Radiant, um den der Pfosten gedreht werden muss
   */
  angle: number;
  /**
   * angles[i] liegt im intervals[i + intervalOffset]
   */
  intervalOffset: number;
  /**
   * für Pfosten-Scoring in getPfostenMetaParameter verwendet, Wertebereich [-1 .. 0]
   */
  score: number;
}

interface MatchingResult {
  matchCount: number;
  typeScoreSum: number;
  deltaMiddles: number;
  rotation: number;
  intervalOffset: number;
  angleWidthUsedSum: number;
}

function getMatchingRotationResult(
  angles: PfostenWinkelInput[],
  intervals: AngleInterval[],
  { typeScoreSum, rotation, intervalOffset, angleWidthUsedSum }: MatchingResult
) {
  const normalIntervals = intervals.filter(([l, r, type]) => type === "").length;
  const unusedIntervals = Math.max(0, angles.length - normalIntervals);

  intervals.map(([l, r, type]) => deltaAngle(l, r));
  const angleWidthSumInFullCircles = angleWidthUsedSum / (2 * Math.PI);

  // Der Score-Wert ist negativ: je kleiner, desto schlechter, Wertebereich [-1 .. 0]
  const score =
    (4 * typeScoreSum - unusedIntervals - angleWidthSumInFullCircles) /
    ((4 * 2 + 1 + 1) * angles.length);

  return { angle: normalizeAngle(rotation), intervalOffset, score };
}

/**
 * Prioritäten: 1. Anzahl Matches, 2. Typ-Score, 3. Winkelabweichung, 4. Kleinste Drehung
 */
function isBetterMatchingResult(current: MatchingResult, best: MatchingResult) {
  return (
    best == null ||
    current.matchCount > best.matchCount ||
    (current.matchCount == best.matchCount &&
      (current.typeScoreSum > best.typeScoreSum ||
        (current.typeScoreSum == best.typeScoreSum &&
          (current.deltaMiddles < best.deltaMiddles ||
            (current.deltaMiddles == best.deltaMiddles &&
              (Number.isNaN(best.rotation) ||
                Math.abs(current.rotation) < Math.abs(best.rotation) ||
                (Math.abs(current.rotation) == Math.abs(best.rotation) &&
                  current.rotation > best.rotation)))))))
  );
}

function findFlacheSeiteRotation(
  intervals: AngleInterval[],
  angles: PfostenWinkelInput[],
  flacheSeiteWinkelInput: PfostenWinkelInput
) {
  let best: MatchingResult = null;

  const remainingAngles = angles.filter((a) => !a.flacheSeiteNotwendig);
  const intervalsLength = intervals.length;

  // Iteriere durch alle Intervalle als potenzielle "Heimat" für den Tor-Winkel
  for (let intervalOffset = 0; intervalOffset < intervalsLength; intervalOffset++) {
    const gateInterval = intervals[intervalOffset];
    const gateIntervalType = gateInterval[2];

    // Überspringe, wenn das Intervall nicht für eine flache Seite geeignet ist (z.B. Eckadapter)
    if (!flacheSeitePasst(flacheSeiteWinkelInput, gateIntervalType)) {
      continue;
    }

    // Berechne die feste Drehung, die nötig ist, um den Tor-Winkel in diesem Intervall zu zentrieren.
    // Dies ist unsere "Kandidaten-Rotation".
    const rotation = normalizeHalfCircle(
      flacheSeiteWinkelInput.angle - middleAngle(gateInterval[0], gateInterval[1])
    );

    // Initialisiere die Metriken für diese Kandidaten-Rotation
    let matchCount = 1; // Der Tor-Winkel selbst ist der erste Match
    let typeScoreSum = typeScore(gateIntervalType);
    let deltaMiddles = 0; // Abweichung für den zentrierten Winkel ist 0
    let angleWidthUsedSum = deltaAngle(gateInterval[0], gateInterval[1]);

    const availableIntervals = intervals.filter((_, idx) => idx !== intervalOffset);
    const usedIntervals = new Set<AngleInterval>(); // Verhindert, dass ein Intervall mehrfach genutzt wird

    // Führe nun eine "Best-Effort"-Zuordnung für die restlichen Winkel durch
    for (const remAngle of remainingAngles) {
      const rotatedAngle = normalizeAngle(remAngle.angle - rotation);

      // Finde ein passendes, noch nicht benutztes Intervall
      for (const availableInterval of availableIntervals) {
        if (
          !usedIntervals.has(availableInterval) &&
          angleInsideInterval(rotatedAngle, availableInterval[0], availableInterval[1])
        ) {
          // Match gefunden!
          matchCount++;
          usedIntervals.add(availableInterval);

          // Metriken für den gefundenen Match aktualisieren
          typeScoreSum += typeScore(availableInterval[2]);
          angleWidthUsedSum += deltaAngle(availableInterval[0], availableInterval[1]);
          const rotatedMiddle = normalizeAngle(
            middleAngle(availableInterval[0], availableInterval[1]) + rotation
          );
          deltaMiddles += Math.abs(normalizeHalfCircle(remAngle.angle - rotatedMiddle));

          // Nimm das erste gefundene Intervall und gehe zum nächsten Winkel
          break;
        }
      }
    }

    deltaMiddles = roundAngle(deltaMiddles);

    const current = {
      matchCount,
      typeScoreSum,
      deltaMiddles,
      rotation,
      intervalOffset,
      angleWidthUsedSum,
    };
    if (isBetterMatchingResult(current, best)) {
      best = current;
    }
  }
  if (best === null) {
    return null;
  }
  return getMatchingRotationResult(angles, intervals, best);
}

function findRotationForEveryAngle(intervals: AngleInterval[], angles: PfostenWinkelInput[]) {
  let best: MatchingResult = null;
  const intervalsLength = intervals.length;
  const anglesLength = angles.length;
  // Summiert pro Angle die Breite des Intervals, in dem angle liegt, um daraus den Durchschnitt
  // zu bilden. Damit können Pfosten, die einen besonders breiten Winkelbereich abdecken, im Scoring
  // benachteiligt werden. Beispiele dafür sind Eck-Steckpfosten, die genau wie normale Steckpfosten
  // die Winkel 0° und 180° erlauben, aber dafür nicht gedacht sind.
  // Einheit in Vollkreisen, also 1 = 360°
  let angleWidthSumInFullCircles = 0;
  // Betrachte die angles als fix und rotiere die Intervalle so, dass die angle möglichst mittig
  // in ihnen liegen.
  // Probiere jedes Intervall als Startintervall aus
  for (let i = 0; i < intervalsLength; i++) {
    const [l0, r0, type] = intervals[i];
    const middle_0 = middleAngle(l0, r0);
    const widthInterval_0 = deltaAngle(l0, r0);
    const firstAngleRotation = deltaAngle(middle_0, angles[0].angle);
    // initialAngleDifference rotiert das initiale Intervall so, dass der erste Winkel mittig darin ist
    const initialAngleDifference = normalizeHalfCircle(angles[0].angle - middle_0);
    let angleDifferenceSum = 0;
    let angleWidthUsedSum = widthInterval_0;
    let deltaMiddles = 0;
    let rotatedIntersection = intervalAddScalar(intervals[i] as any, firstAngleRotation);
    // Tore müssen immer zur flachen Seite ausgerichtet sein
    let flacheSeiteMatchCount = flacheSeitePasst(angles[0], type) ? 1 : 0;
    // Ausrichtungen ohne Eckadapter / UM-Profile werden bevorzugt
    let typeScoreSum = typeScore(type);
    let angleIndex = 1;
    // Prüfe, ob die Winkel durch Intervalle abgedeckt sind
    // Das ist dann der Fall, wenn eine Schnittmenge aller aufeinander abgebildeten Intervalle
    // existiert. Dazu werden die angle aufeinander gedreht und die intervalle mitrotiert.
    for (let j = 1; j < intervalsLength && angleIndex < anglesLength; j++) {
      const interval_j = intervals[(i + j) % intervalsLength];
      // Berechne den Winkelversatz zwischen dem aktuellen Winkel und dem ersten Winkel
      const offset = deltaAngle(angles[angleIndex].angle, angles[0].angle);
      // Gesamte Rotation für das aktuelle Intervall: initiale Rotation plus zusätzlicher Versatz
      // Durch Addition von offset und firstAngleRotation erhalten wir die Gesamtrotation,
      // die auf das aktuelle Intervall angewendet werden muss, um es so zu verschieben,
      // dass sein Mittelpunkt mit dem aktuellen Winkel angles[angleIndex] ausgerichtet ist.
      // Dies berücksichtigt sowohl die initiale Rotation als auch die relative Position des aktuellen Winkels.
      const rotatedInterval_j = intervalAddScalar(interval_j as any, offset + firstAngleRotation);
      const intersection = intervalIntersection(rotatedIntersection, rotatedInterval_j);
      if (intersection) {
        const middleInterval = middleAngle(interval_j[0], interval_j[1]);
        const widthInterval = deltaAngle(interval_j[0], interval_j[1]);
        // Berechne die normalisierte Winkelabweichung (beta) zwischen angles[angleIndex] und
        // dem Mittelpunkt von middleInterval nach Rotation. Beta misst, wie nah der aktuelle
        // Winkel am Zentrum des rotierenden Intervalls liegt. Eine kleinere Abweichung ist wünschenswert.
        // Durch offset + firstAngleRotation wird das Intervall ausgerichtet, während die relativen
        // Positionen erhalten bleiben.
        const beta = normalizeHalfCircle(
          angles[angleIndex].angle - (middleInterval + initialAngleDifference)
        );
        angleDifferenceSum += beta;
        deltaMiddles += Math.abs(beta);
        angleWidthUsedSum += widthInterval;
        const type = interval_j[2];
        flacheSeiteMatchCount += flacheSeitePasst(angles[angleIndex], type) ? 1 : 0;
        typeScoreSum += typeScore(type);
        rotatedIntersection = intersection;
        angleIndex++;
      } else {
        continue;
      }
    }
    // runde die Werte etwas, um Floating-Point-Bugs auszugleichen => dadurch werden eher natürliche Werte wie 0° bevorzugt
    deltaMiddles = roundAngle(deltaMiddles);
    // Wenn die Rotation gültig ist: nimm die beste
    const rotation = roundAngle(
      normalizeHalfCircle(angleDifferenceSum / anglesLength + initialAngleDifference)
    );

    const current = {
      matchCount: flacheSeiteMatchCount,
      typeScoreSum,
      deltaMiddles,
      rotation,
      intervalOffset: i,
      angleWidthUsedSum,
    };
    if (
      rotatedIntersection != null &&
      angleIndex === anglesLength &&
      isBetterMatchingResult(current, best)
    ) {
      best = current;
    }
  }

  if (best === null) {
    return null;
  }
  return getMatchingRotationResult(angles, intervals, best);
}

/**
 * Findet eine passende und minimale Rotation, sodass jeder Winkel in einem Intervall liegt und nie
 * zwei Winkel im selben Intervall sind. Bei mehreren möglichen Rotationen wird diejenige gewählt,
 * die die maximale Anzahl an hohen Prioritätsintervallen abdeckt.
 * Falls auch Winkel mit hohen Prioritäten existieren, werden Drehungen bevorzugt, die Winkel mit
 * hoher Priorität in Intervalle mit hoher Priorität rotiert.
 * Die Rotation liegt am Ende immer mittig in der Intervallschnittmenge.
 * Falls die Winkel durch die Intervalle nicht abgedeckt werden können, wird NaN zurückgegeben.
 * @param intervals Array von Intervallen in Radiant
 * @param angles_ Array von PfostenWinkelInput in Radiant
 * @returns MatchingRotation oder null
 */
export function findMatchingRotation(
  intervals: AngleInterval[],
  angles_: PfostenWinkelInput[]
): MatchingRotation | null {
  if (angles_.length === 0) {
    return { angle: 0, intervalOffset: 0, score: 0 };
  }
  if (intervals.length === 0 || angles_.length > intervals.length) {
    return null;
  }
  const angles: PfostenWinkelInput[] = angles_
    .map(({ angle, flacheSeiteNotwendig }) => ({
      angle: normalizeAngle(angle),
      flacheSeiteNotwendig,
    }))
    .toSorted((a, b) => a.angle - b.angle);

  const flacheSeiteWinkelInput = angles.find((a) => a.flacheSeiteNotwendig);

  if (flacheSeiteWinkelInput) {
    return findFlacheSeiteRotation(intervals, angles, flacheSeiteWinkelInput);
  } else {
    return findRotationForEveryAngle(intervals, angles);
  }
}

export function getAngleIntervalType(pfosten: Pfosten, angle: number): AngleIntervalType {
  if (pfosten?.matchingRotation == null || pfosten?.meta == null) {
    return "";
  }
  const matchingRotation = pfosten.matchingRotation;
  const searchAngle = normalizeAngle(angle - matchingRotation.angle);
  const intervals = (pfosten.meta as MPfosten)?.WinkelbereicheParsed;
  const index = intervals.findIndex(([l, r]) => angleInsideInterval(searchAngle, l, r));
  if (index === -1) return "";
  const [l, r, type] = intervals[index];
  const toDegree = (a) => parseFloat(roundAngle((a * 180) / Math.PI).toFixed(5));
  return type ?? "";
}

/**
 * Prüft, ob zwei Winkel in einem bestimmten Vielfachen zueinander stehen.
 *
 * @param a Erster Winkel in Radiant
 * @param b Zweiter Winkel in Radiant
 * @param multipleOf Das Winkelmaß in Radiant, dessen Vielfaches geprüft werden soll (z.B. pi/2 für 90°).
 * @param epsilon Eine Winkeltoleranz in Radiant. Standard ist 1e-9.
 * @returns True, wenn die Winkel in einem Vielfachen von `multipleOf` zueinander stehen, sonst False.
 */
export function sindWinkelVielfache(
  a: number,
  b: number,
  multipleOf: number,
  epsilon = 1e-9
): boolean {
  // Edge-Case: Wenn das Vielfache 0 oder sehr klein ist, prüfen wir auf Gleichheit der Winkel.
  if (Math.abs(multipleOf) < 1e-12) {
    const diff = normalizeAngle(a - b);
    // Prüfen, ob der kürzeste Abstand (in beide Richtungen des Kreises) innerhalb der Toleranz liegt.
    return Math.min(diff, fullCircle - diff) < epsilon;
  }

  // Die Differenz zwischen den Winkeln berechnen. `normalizeAngle` gibt immer einen positiven Wert zurück.
  const diff = normalizeAngle(a - b);

  // Berechnen, wie oft `multipleOf` in die Differenz passt.
  const quotient = diff / multipleOf;

  // Berechnen, wie nah der Quotient an der nächsten ganzen Zahl ist (das ist der Fehler-Anteil des Quotienten).
  const distanceToNearestInt = Math.abs(quotient - Math.round(quotient));

  // Diesen Fehler zurück in einen Winkel umrechnen und mit der Toleranz vergleichen.
  // Dies ist der absolute Winkel-Fehler zum nächsten Vielfachen von `multipleOf`.
  const angularError = distanceToNearestInt * multipleOf;

  return angularError < epsilon;
}
